/**
 * ForgotPassword.jsx — Two-step password reset.
 * Step 1: Enter email → receive OTP
 * Step 2: Enter OTP + new password → reset
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, IconButton,
  InputAdornment, Paper, TextField, Typography,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { apiClient } from '../utils/auth';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = enter email, 2 = enter OTP + new password, 3 = done
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setFieldErrors({ email: 'Email is required.' }); return; }
    setLoading(true); setError(''); setFieldErrors({});
    try {
      await apiClient.post('/api/v1/auth/password/forgot/', { email: email.trim().toLowerCase() });
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!otp.trim()) errs.otp = 'OTP is required.';
    if (!newPassword) errs.newPassword = 'New password is required.';
    else if (newPassword.length < 8) errs.newPassword = 'Password must be at least 8 characters.';
    else if (!/[A-Z]/.test(newPassword)) errs.newPassword = 'Must contain at least one uppercase letter.';
    else if (!/[0-9]/.test(newPassword)) errs.newPassword = 'Must contain at least one number.';
    if (newPassword !== confirmPassword) errs.confirmPassword = 'Passwords do not match.';
    if (Object.keys(errs).length) { setFieldErrors(errs); return; }

    setLoading(true); setError(''); setFieldErrors({});
    try {
      await apiClient.post('/api/v1/auth/password/reset/', {
        email: email.trim().toLowerCase(),
        otp: otp.trim(),
        new_password: newPassword,
      });
      setStep(3);
    } catch (err) {
      const code = err.response?.data?.error?.code;
      const msg = err.response?.data?.error?.message || 'Reset failed. Please try again.';
      if (code === 'INVALID_OTP') setFieldErrors({ otp: 'Incorrect OTP. Please check and try again.' });
      else if (code === 'OTP_EXPIRED') setError('This OTP has expired. Please go back and request a new one.');
      else if (code === 'WEAK_PASSWORD') setFieldErrors({ newPassword: msg });
      else setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
      <Paper elevation={3} sx={{ maxWidth: 440, width: '100%', p: 4, borderRadius: 2 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <LockResetIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="h5" fontWeight={700}>Reset Password</Typography>
        </Box>

        {/* Step 1 — Enter email */}
        {step === 1 && (
          <Box component="form" onSubmit={handleSendOTP} noValidate>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter your registered email address and we'll send you a 6-digit OTP to reset your password.
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
              label="Email Address" type="email" value={email}
              onChange={e => { setEmail(e.target.value); setFieldErrors({}); }}
              error={!!fieldErrors.email} helperText={fieldErrors.email}
              fullWidth required margin="normal" autoFocus
            />
            <Button type="submit" variant="contained" fullWidth size="large"
              disabled={loading} sx={{ mt: 2 }}>
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Send OTP'}
            </Button>
            <Button fullWidth sx={{ mt: 1 }} onClick={() => navigate('/login')}>
              Back to Login
            </Button>
          </Box>
        )}

        {/* Step 2 — Enter OTP + new password */}
        {step === 2 && (
          <Box component="form" onSubmit={handleReset} noValidate>
            <Alert severity="info" sx={{ mb: 2 }}>
              An OTP has been sent to <strong>{email}</strong>. Check your inbox (and spam folder).
              It's valid for 15 minutes.
            </Alert>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
              label="6-digit OTP" value={otp}
              onChange={e => { setOtp(e.target.value.replace(/\D/g, '')); setFieldErrors(f => ({ ...f, otp: '' })); }}
              error={!!fieldErrors.otp} helperText={fieldErrors.otp}
              fullWidth required margin="normal" inputProps={{ maxLength: 6 }} autoFocus
            />
            <TextField
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={e => { setNewPassword(e.target.value); setFieldErrors(f => ({ ...f, newPassword: '' })); }}
              error={!!fieldErrors.newPassword}
              helperText={fieldErrors.newPassword || 'Min 8 chars, 1 uppercase, 1 number'}
              fullWidth required margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(s => !s)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirm New Password"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={e => { setConfirmPassword(e.target.value); setFieldErrors(f => ({ ...f, confirmPassword: '' })); }}
              error={!!fieldErrors.confirmPassword} helperText={fieldErrors.confirmPassword}
              fullWidth required margin="normal"
            />
            <Button type="submit" variant="contained" fullWidth size="large"
              disabled={loading} sx={{ mt: 2 }}>
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Reset Password'}
            </Button>
            <Button fullWidth sx={{ mt: 1 }} onClick={() => setStep(1)}>
              ← Back (resend OTP)
            </Button>
          </Box>
        )}

        {/* Step 3 — Done */}
        {step === 3 && (
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 56, mb: 2 }} />
            <Typography variant="h6" gutterBottom>Password Reset Successfully</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Your password has been updated. You can now sign in with your new password.
            </Typography>
            <Button variant="contained" fullWidth onClick={() => navigate('/login')}>
              Sign In
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
